import {
  HexagonIcon,
  KeyIcon,
  LightningIcon,
  RobotIcon,
  ShieldCheckIcon,
  SquaresFourIcon,
  TerminalWindowIcon,
} from '@phosphor-icons/react';
import { Helmet } from 'react-helmet-async';

interface FeatureItem {
  title: string;
  description: string;
  icon: 'robot' | 'grid' | 'lightning' | 'terminal' | 'shield' | 'key';
}

interface StatItem {
  value: string;
  label: string;
}

interface AuthLayoutProps {
  children: React.ReactNode;
  headline: React.ReactNode;
  subline: string;
  features: FeatureItem[];
  /** Omitted on pages where marketing numbers would be out of place, e.g. device approval. */
  stats?: StatItem[];
  /** Page title for Helmet */
  pageTitle?: string;
}

const FEATURE_ICONS = {
  robot: RobotIcon,
  grid: SquaresFourIcon,
  lightning: LightningIcon,
  terminal: TerminalWindowIcon,
  shield: ShieldCheckIcon,
  key: KeyIcon,
} as const;

const ORB_STYLES = [
  {
    className: 'absolute size-150 rounded-full',
    style: {
      left: 80,
      top: 40,
      background: 'radial-gradient(circle, rgba(129,140,248,0.3) 0%, transparent 70%)',
    } as React.CSSProperties,
    animation: 'animate-auth-pulse',
  },
  {
    className: 'absolute size-125 rounded-full',
    style: {
      left: 350,
      top: 400,
      background: 'radial-gradient(circle, rgba(192,132,252,0.2) 0%, transparent 70%)',
      animationDelay: '2s',
    } as React.CSSProperties,
    animation: 'animate-auth-pulse',
  },
  {
    className: 'absolute size-100 rounded-full',
    style: {
      left: -80,
      top: 550,
      background: 'radial-gradient(circle, rgba(56,189,248,0.12) 0%, transparent 70%)',
      animationDelay: '4s',
    } as React.CSSProperties,
    animation: 'animate-auth-pulse',
  },
] as const;

const FLOAT_SHAPES = [
  {
    style: { '--float-rotate': '15deg', width: 64, height: 64, left: 560, top: 180 } as React.CSSProperties,
    className: 'rounded-xl animate-auth-float',
  },
  {
    style: {
      '--float-rotate': '-20deg',
      width: 44,
      height: 44,
      left: 620,
      top: 500,
      animationDelay: '1s',
      animationDuration: '8s',
    } as React.CSSProperties,
    className: 'rounded-lg animate-auth-float',
  },
  {
    style: {
      '--float-rotate': '30deg',
      width: 52,
      height: 52,
      left: 480,
      top: 650,
      animationDelay: '3s',
      animationDuration: '7s',
    } as React.CSSProperties,
    className: 'rounded-xl animate-auth-float',
  },
] as const;

export function AuthLayout({ children, headline, subline, features, stats, pageTitle }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen w-full bg-canvas">
      {pageTitle && (
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content="Cloud workspaces where you and AI agents build together." />
        </Helmet>
      )}

      {/* Left Panel — rich gradient background (hidden below lg) */}
      <div
        className="relative w-190 shrink-0 overflow-hidden hidden xl:block"
        style={{
          background: 'linear-gradient(145deg, #1e1b4b 0%, #312e81 35%, #3730a3 60%, #4338ca 100%)',
        }}
      >
        {/* Dot grid background */}
        <div
          className="absolute inset-0 opacity-18"
          style={{
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        {/* Vivid glow orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {ORB_STYLES.map((orb, i) => (
            <div key={i} className={`${orb.className} ${orb.animation}`} style={orb.style} />
          ))}
        </div>

        {/* Floating decorative shapes */}
        <div className="absolute inset-0 pointer-events-none">
          {FLOAT_SHAPES.map((shape, i) => (
            <div
              key={i}
              className={`absolute bg-white/6 border border-white/12 backdrop-blur-sm ${shape.className}`}
              style={shape.style}
            />
          ))}
        </div>

        {/* Left content */}
        <div className="relative z-10 flex h-full max-w-158 flex-col justify-between p-16">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <HexagonIcon size={28} weight="fill" className="text-white" />
            <span className="font-mono text-[22px] font-bold text-white">zooid</span>
          </div>

          {/* Middle: headline + features */}
          <div className="flex flex-1 flex-col justify-center gap-8">
            <div className="flex flex-col gap-4">
              <h1 className="max-w-140 text-5xl leading-[1.08] font-extrabold text-white tracking-tight">{headline}</h1>
              <p className="max-w-120 text-base leading-relaxed text-white/60">{subline}</p>
            </div>

            <div className="flex flex-col gap-5">
              {features.map((feat) => {
                const IconComponent = FEATURE_ICONS[feat.icon];
                return (
                  <div key={feat.title} className="flex items-center gap-3.5">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/10 border border-white/12">
                      <IconComponent size={20} weight="fill" className="text-white" />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-white">{feat.title}</span>
                      <span className="text-xs text-white/50">{feat.description}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom: stats */}
          {stats && stats.length > 0 && (
            <div className="flex gap-10">
              {stats.map((stat) => (
                <div key={stat.label} className="flex flex-col gap-1">
                  <span className="font-mono text-2xl font-bold text-white">{stat.value}</span>
                  <span className="text-xs text-white/45">{stat.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel — theme-aware surface (light: near-white, dark: deep canvas) */}
      <div className="relative flex flex-1 items-center justify-center bg-canvas pt-20 pb-8 xl:py-0">
        {/* Subtle gradient mesh — light */}
        <div
          className="absolute inset-0 pointer-events-none opacity-60 dark:hidden"
          style={{
            background:
              'radial-gradient(ellipse at 30% 20%, rgba(199,210,254,0.4) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(232,213,255,0.3) 0%, transparent 50%)',
          }}
        />
        {/* Subtle gradient mesh — dark (indigo/violet glow on deep canvas) */}
        <div
          className="absolute inset-0 pointer-events-none hidden dark:block"
          style={{
            background:
              'radial-gradient(ellipse at 30% 20%, rgba(80,72,232,0.22) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(168,85,247,0.16) 0%, transparent 50%)',
          }}
        />

        {/* Mobile brand bar — only visible below lg */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-center px-4 py-5 xl:hidden">
          <HexagonIcon size={22} weight="fill" className="text-brand" />
          <span className="ml-2 font-mono text-lg font-bold text-fg">zooid</span>
        </div>

        <div className="relative z-10 w-full max-w-140 px-4 sm:px-8 xl:px-15">{children}</div>
      </div>
    </div>
  );
}
